import Link from "next/link";
import type { ComponentProps, ReactNode } from "react";

type Variant = "primary" | "secondary" | "ghost";
type Size = "sm" | "md" | "lg";

interface BaseProps {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
  className?: string;
}

interface LinkButtonProps extends BaseProps {
  href: string;
  external?: boolean;
  onClick?: () => void;
}

type ButtonProps = BaseProps &
  Omit<ComponentProps<"button">, keyof BaseProps>;

function variantClasses(v: Variant): string {
  if (v === "primary") {
    return "bg-[var(--color-accent)] text-[var(--color-bg)] border-transparent hover:shadow-[var(--shadow-glow-accent)] hover:brightness-110";
  }
  if (v === "secondary") {
    return "bg-[var(--color-surface)] text-[var(--color-ink)] border-[var(--color-border)] hover:border-[var(--color-border-hover)]";
  }
  // ghost
  return "bg-transparent text-[var(--color-mute)] hover:text-[var(--color-ink)] border-transparent";
}

function sizeClasses(s: Size): string {
  if (s === "sm") return "px-4 py-2";
  if (s === "lg") return "px-8 py-4";
  return "px-[22px] py-[14px]";
}

const base =
  "inline-flex items-center justify-center rounded-[var(--radius-full)] border font-sans font-medium text-[14px] tracking-[-0.01em] transition-all duration-150 no-underline";

/** Pill button — primary / secondary / ghost. */
export function TallerButton({
  variant = "primary",
  size = "md",
  children,
  className = "",
  ...rest
}: ButtonProps) {
  return (
    <button
      className={`${base} ${sizeClasses(size)} ${variantClasses(variant)} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

/** Link-as-button variant — same shape, renders a Next.js Link. */
export function TallerButtonLink({
  variant = "primary",
  size = "md",
  href,
  external = false,
  children,
  className = "",
  ...rest
}: LinkButtonProps) {
  const classes = `${base} ${sizeClasses(size)} ${variantClasses(variant)} ${className}`;
  if (external) {
    return (
      <a className={classes} href={href} target="_blank" rel="noopener noreferrer" {...rest}>
        {children}
      </a>
    );
  }
  return (
    <Link className={classes} href={href} {...rest}>
      {children}
    </Link>
  );
}
